import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import HoneyAcornMark from "@/brand/HoneyAcornMark";

export default function ResetPassword() {
  const { updatePassword, isSupabaseConfigured } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmation) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      toast.success("Password updated. You can sign in now.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not update your password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background px-6 py-12 sm:px-12" data-testid="reset-password-page">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <HoneyAcornMark size={30} />
          <div>
            <div className="font-heading text-lg font-bold tracking-tight text-neutral-900">
              HoneyAcorn
            </div>
            <div className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-neutral-500">
              Childcare, connected
            </div>
          </div>
        </div>

        <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Set a new password
        </h1>
        <p className="mt-2 text-base text-neutral-600">
          Choose a password with at least 8 characters.
        </p>

        {!isSupabaseConfigured && (
          <div className="mt-6 rounded-2xl border border-secondary/60 bg-secondary/20 p-4 text-sm text-neutral-800">
            Password recovery is only available when HoneyAcorn is connected to
            Supabase.
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="reset-password-form">
          <div>
            <label htmlFor="reset-password" className="mb-1.5 block text-sm font-medium text-neutral-800">
              New password
            </label>
            <input
              id="reset-password"
              name="password"
              data-testid="reset-password-input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div>
            <label htmlFor="reset-password-confirmation" className="mb-1.5 block text-sm font-medium text-neutral-800">
              Confirm new password
            </label>
            <input
              id="reset-password-confirmation"
              name="password-confirmation"
              data-testid="reset-password-confirmation-input"
              type="password"
              required
              minLength={8}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <button
            type="submit"
            data-testid="reset-password-submit"
            disabled={submitting || !isSupabaseConfigured}
            className="w-full rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Updating password…" : "Update password"}
          </button>
          <p className="text-center text-sm text-neutral-500">
            <Link to="/login" className="font-medium text-primary-hover hover:underline">
              Return to sign in
            </Link>
          </p>
        </form>
      </div>
    </main>
  );
}
